import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { Page, PageHeader } from '../../shared/ui/layout';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import { getSource } from '../../entities/source';
import { AddClient, SourceClients } from '../../widgets/source-clients';

interface ISourceClientsPageProps {
  sourceId: string;
  timeZone?: string;
  search: string;
  onSearch: (search: string) => void;
  onOpenClient: (id: string) => void;
  onSetUpSigning: () => void;
}

export const SourceClientsPage = ({
  sourceId,
  timeZone,
  search,
  onSearch,
  onOpenClient,
  onSetUpSigning,
}: ISourceClientsPageProps) => {
  const source = useQuery({ queryKey: ['source', sourceId], queryFn: () => getSource(sourceId) });

  if (source.isError) {
    return (
      <div className="p-page">
        <ErrorState error={source.error} onRetry={() => void source.refetch()} />
      </div>
    );
  }

  if (!source.data) {
    return (
      <div className="p-page">
        <LoadingState rows={8} />
      </div>
    );
  }

  const data = source.data;

  return (
    <>
      <PageHeader
        width="full"
        title="Clients"
        breadcrumbs={
          <span className="flex min-w-0 items-center gap-2">
            <Link to="/sources">Sources</Link>
            <span className="text-faint">/</span>
            <Link to="/sources/$sourceId" params={{ sourceId }} className="truncate">
              {data.name}
            </Link>
          </span>
        }
        description={
          data.clientSecrets
            ? 'Each one gets its own address and its own secret. Open a client to see everything about it.'
            : 'Each one gets its own address.'
        }
        actions={<AddClient source={data} onAdded={(added) => onOpenClient(added.customer.id)} />}
      />
      <Page width="full">
        <SourceClients
          source={data}
          search={search}
          onSearch={onSearch}
          onOpenClient={onOpenClient}
          onSetUpSigning={onSetUpSigning}
          timeZone={timeZone}
        />
      </Page>
    </>
  );
};
